var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ConfigFieldControl, DragArea, readTextFile } from '@sierralabs/core-ui';
import { EntityPicker, useEntityContext } from '@sierralabs/entity-ui';
import { useCreateFormTemplate } from '../FormTemplate.hooks';
export const ImportFormTemplateDialog = props => {
    const { open, onCancel, onImport } = props;
    const onImportRef = useRef(onImport);
    const fileInputRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [importForm, setImportForm] = useState();
    const [importedEntityId, setImportedEntityId] = useState();
    const { entities } = useEntityContext();
    const { createdFormTemplate, isCreating, createError, createFormTemplate } = useCreateFormTemplate();
    const dynamicEntities = useMemo(() => {
        return (entities === null || entities === void 0 ? void 0 : entities.filter(e => e.id)) || [];
    }, [entities]);
    const entityExists = useMemo(() => {
        const entityId = importForm === null || importForm === void 0 ? void 0 : importForm.entityId;
        return !!entityId && dynamicEntities.some(e => e.id === entityId);
    }, [dynamicEntities, importForm === null || importForm === void 0 ? void 0 : importForm.entityId]);
    const importedEntityExists = useMemo(() => {
        return (!!importedEntityId &&
            dynamicEntities.some(e => e.id === importedEntityId));
    }, [dynamicEntities, importedEntityId]);
    const isValid = !!(importForm === null || importForm === void 0 ? void 0 : importForm.name) && entityExists;
    const showWarning = !!importedEntityId &&
        (importForm === null || importForm === void 0 ? void 0 : importForm.entityId) === importedEntityId &&
        !importedEntityExists;
    const onClose = () => {
        if (!isCreating) {
            onCancel === null || onCancel === void 0 ? void 0 : onCancel();
        }
    };
    const handleFiles = (files) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!files || files.length === 0) {
            return;
        }
        const result = yield readTextFile(files[0], value => {
            var _a;
            const form = JSON.parse(value || '');
            if (((_a = form.entity) === null || _a === void 0 ? void 0 : _a.id) && !form.entityId) {
                form.entityId = form.entity.id;
            }
            return form;
        });
        if (result) {
            const { value, error = '' } = result;
            setImportForm(value);
            setImportedEntityId((value === null || value === void 0 ? void 0 : value.entityId) || ((_a = value === null || value === void 0 ? void 0 : value.entity) === null || _a === void 0 ? void 0 : _a.id));
            setErrorMessage(error);
        }
    });
    const handleSubmit = () => {
        if (isValid && !isCreating) {
            createFormTemplate(importForm);
        }
    };
    useEffect(() => {
        onImportRef.current = onImport;
    }, [onImport]);
    // reset values
    useEffect(() => {
        if (!open) {
            setImportForm(undefined);
            setImportedEntityId(undefined);
            setErrorMessage('');
        }
    }, [open]);
    useEffect(() => {
        var _a;
        if (createdFormTemplate) {
            (_a = onImportRef.current) === null || _a === void 0 ? void 0 : _a.call(onImportRef, createdFormTemplate);
        }
    }, [createdFormTemplate]);
    useEffect(() => {
        if (createError) {
            setErrorMessage(createError.message);
        }
    }, [createError]);
    return (React.createElement(Dialog, { fullWidth: true, open: open, onClose: onClose },
        isCreating && React.createElement(LinearProgress, null),
        React.createElement(DialogTitle, null, "Import Form Template"),
        React.createElement(DialogContent, null,
            React.createElement(DragArea, { disabled: isCreating, dragLabel: 'Drag exported Form JSON file to import', buttonLabel: 'Select Import File', onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, onUpload: handleFiles }),
            importForm && (React.createElement(Box, { mt: 2 },
                React.createElement(ImportFormTemplateForm, { value: importForm, disabled: isCreating, onSubmit: handleSubmit, onChange: dto => setImportForm(Object.assign(Object.assign({}, importForm), dto)) }))),
            React.createElement(Collapse, { in: showWarning, style: { minHeight: 'unset' } },
                React.createElement(Box, { mt: 2 },
                    React.createElement(Alert, { severity: 'warning' },
                        "The referenced ",
                        React.createElement("strong", null, "Entity"),
                        " from the imported",
                        ' ',
                        React.createElement("strong", null, "Form Template"),
                        " does not exist. Please select another Entity to be able to import the Form Template."))),
            React.createElement(Collapse, { in: !!errorMessage, style: { minHeight: 'unset' } },
                React.createElement(Box, { mt: 2 },
                    React.createElement(Alert, { severity: 'error' }, errorMessage || 'An error occurred.'))),
            React.createElement("input", { hidden: true, type: 'file', accept: 'application/json', ref: fileInputRef, onChange: event => {
                    handleFiles(event.target.files);
                    // clear input field
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                } })),
        React.createElement(DialogActions, null,
            React.createElement(Button, { disabled: isCreating, onClick: onClose }, "Cancel"),
            React.createElement(Button, { color: 'secondary', variant: 'contained', disabled: !isValid || isCreating, onClick: () => handleSubmit() }, "Import Form Template"))));
};
export default ImportFormTemplateDialog;
const ImportFormTemplateForm = props => {
    const { value, disabled, onSubmit, onChange } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', variant: 'outlined', label: 'Form Template Name', disabled: disabled, error: !value.name, value: value.name || '', onKeyPress: event => {
                    if (event.key === 'Enter') {
                        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
                    }
                }, onChange: event => onChange({ name: event.target.value }) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(EntityPicker, { fullWidth: true, disabled: disabled, value: value.entityId, label: 'Select Entity', placeholder: 'Select an Entity for the Form Template', filter: entity => !!entity.id, onChange: entity => onChange({ entityId: entity === null || entity === void 0 ? void 0 : entity.id }) }))));
};
//# sourceMappingURL=ImportFormTemplateDialog.js.map